import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import type { ChipKey } from '../utils/lineFilters';
import { Chip } from './FilterChip';

interface FocusPickerProps {
  options: MultiSelectOption[];
  value: MultiSelectOption[];
  onChange: (next: MultiSelectOption[]) => void;
  placeholder: string;
  widthClass: string;
}

function FocusPicker({
  options,
  value,
  onChange,
  placeholder,
  widthClass,
}: FocusPickerProps) {
  if (options.length <= 1) return null;
  return (
    <div className={widthClass}>
      <MultiSelect
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        size="sm"
        fullWidth
        hideChipsWhenAllSelected
      />
    </div>
  );
}

function SearchBox({
  search,
  onSearch,
  gt,
}: {
  search: string;
  onSearch: (next: string) => void;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <div className="relative w-64">
      <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
      <Input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={gt('Search SKU, name, warehouse', {
          context: 'assignment board search placeholder',
        })}
        className="h-7 pl-7 pr-7 text-xs"
      />
      {search && (
        <button
          type="button"
          onClick={() => onSearch('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          aria-label={gt('Clear search', {
            context: 'assignment board search clear',
          })}
        >
          <XIcon className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

/** "N selected · Clear selection", shown while any line is selected. */
function LineSelectionControl({
  count,
  onClear,
}: {
  count: number;
  onClear: () => void;
}) {
  const gt = useGT();
  if (count === 0) return null;
  return (
    <span className="flex items-center gap-1 text-xs text-gray-600 tabular-nums">
      {gt('{count} selected', {
        context: 'assignment board toolbar selected line count',
        count: String(count),
      })}
      <Button
        size="sm"
        variant="ghost"
        className="h-6 gap-1 px-2 text-xs"
        onClick={onClear}
        title={gt('Clear selection (Esc)', {
          context: 'assignment board toolbar clear line selection tooltip',
        })}
      >
        <XIcon className="h-3 w-3" />
        <T context="assignment board toolbar clear line selection">
          Clear selection
        </T>
      </Button>
    </span>
  );
}

function ChipFilters({
  chips,
  onToggleChip,
}: {
  chips: Set<ChipKey>;
  onToggleChip: (key: ChipKey) => void;
}) {
  return (
    <>
      <Chip active={chips.has('sos')} onClick={() => onToggleChip('sos')}>
        <T context="assignment board chip sos">SOs only</T>
      </Chip>
      <Chip active={chips.has('potf')} onClick={() => onToggleChip('potf')}>
        <T context="assignment board chip potf">POTF</T>
      </Chip>
      <Chip
        active={chips.has('deficits')}
        onClick={() => onToggleChip('deficits')}
      >
        <T context="assignment board chip deficits">Deficits</T>
      </Chip>
      <Chip active={chips.has('core')} onClick={() => onToggleChip('core')}>
        <T context="assignment board chip core, items in a core-offer program">
          Core
        </T>
      </Chip>
      <Chip
        active={chips.has('below_mov')}
        onClick={() => onToggleChip('below_mov')}
      >
        <T context="assignment board chip below mov">{'<MOV'}</T>
      </Chip>
      <Chip
        active={chips.has('below_freight')}
        onClick={() => onToggleChip('below_freight')}
      >
        <T context="assignment board chip below freight">{'<Freight'}</T>
      </Chip>
    </>
  );
}

export interface FocusPickerState {
  options: MultiSelectOption[];
  value: MultiSelectOption[];
  onChange: (next: MultiSelectOption[]) => void;
}

interface AssignmentToolbarProps {
  search: string;
  onSearch: (next: string) => void;
  chips: Set<ChipKey>;
  onToggleChip: (key: ChipKey) => void;
  shipTo: FocusPickerState;
  warehouse: FocusPickerState;
  hub: FocusPickerState;
  filteredCount: number;
  scopedCount: number;
  /** Lines selected for a bulk edit (or the one open in the Line section). */
  selectedCount: number;
  onClearSelection: () => void;
  /** The line table's view menu, at the far end. */
  viewMenu?: ReactNode;
  gt: ReturnType<typeof useGT>;
}

/** Search + chip filters + ship-to / warehouse / hub focus pickers. */
export function AssignmentToolbar({
  search,
  onSearch,
  chips,
  onToggleChip,
  shipTo,
  warehouse,
  hub,
  filteredCount,
  scopedCount,
  selectedCount,
  onClearSelection,
  viewMenu,
  gt,
}: AssignmentToolbarProps) {
  const anyPicker = [shipTo, warehouse, hub].some((p) => p.options.length > 1);
  return (
    <div className="flex flex-wrap items-center gap-2 pb-2">
      <SearchBox search={search} onSearch={onSearch} gt={gt} />
      <ChipFilters chips={chips} onToggleChip={onToggleChip} />

      {/* Focus the line table on specific ship-tos / destination
          warehouses / hubs. Empty = show all; selections combine
          (ship-to AND warehouse AND hub). The ship-to filter scopes by PO
          cart; its table column was folded into Warehouse (GEN-2688). */}
      {anyPicker && (
        <span className="mx-0.5 h-4 w-px bg-gray-200" aria-hidden />
      )}
      <FocusPicker
        {...shipTo}
        placeholder={gt('Ship-to / PO', {
          context: 'cart line table column header',
        })}
        widthClass="w-44"
      />
      <FocusPicker
        {...warehouse}
        placeholder={gt('Warehouse', {
          context: 'cart line table column header',
        })}
        widthClass="w-44"
      />
      <FocusPicker
        {...hub}
        placeholder={gt('Hub', { context: 'cart line table column header' })}
        widthClass="w-40"
      />

      <span className="text-xs text-gray-500 tabular-nums">
        {filteredCount === scopedCount
          ? `${scopedCount}`
          : `${filteredCount} / ${scopedCount}`}{' '}
        <T context="assignment board toolbar lines suffix">lines</T>
      </span>
      <LineSelectionControl count={selectedCount} onClear={onClearSelection} />
      {viewMenu && <span className="ml-auto">{viewMenu}</span>}
    </div>
  );
}
